import { createAsyncThunk } from '@reduxjs/toolkit';

import { client } from 'src/graphql/client';
import { CANCEL_PROVIDER_ROUTE } from 'src/graphql/requests/cancelProviderRoute';
import { GCancelProviderRouteMutation } from 'src/graphql/types/generated';
import { logWarning } from 'src/services/datadog';
import type { AppDispatch } from 'src/state/store';
import { firstUserErrorMessage } from 'src/utils';
import { ProviderRouteResult } from './markRouteForProvider';
import { refetchRouteList } from './refetchRouteList';

type CancelProviderRouteArgs = {
  routeId: string;
  warehouseId: string;
  nightEnd: string;
};

// Asks the provider to cancel a dispatched route. Success means the request
// is out, not that the route is back with Odeko, so there is no toast: the
// courier dialog waits on its poll for the provider's answer.
export const cancelProviderRoute = createAsyncThunk(
  'delivery/cancelProviderRoute',
  async (
    { routeId, warehouseId, nightEnd }: CancelProviderRouteArgs,
    thunkAPI,
  ): Promise<ProviderRouteResult> => {
    const { data, error } = await client
      .mutation<GCancelProviderRouteMutation>(CANCEL_PROVIDER_ROUTE, {
        input: { routeId },
      })
      .toPromise();

    // Every outcome can change the route: a refusal may have written the
    // provider's reason, and a lost response may hide a pending cancel.
    refetchRouteList(thunkAPI.dispatch as AppDispatch, {
      warehouseId,
      nightEnd,
    });

    const payload = data?.cancelProviderRoute;
    const userErrors = payload?.userErrors ?? [];

    if (userErrors.length > 0) {
      logWarning('third_party.cancel_route_failed', {
        routeId,
        userError: userErrors[0]?.__typename,
        transportError: false,
      });
      return { ok: false, message: firstUserErrorMessage(userErrors) };
    }

    if (error || !payload) {
      logWarning('third_party.cancel_route_failed', {
        routeId,
        transportError: Boolean(error),
      });
      return { ok: false };
    }

    return { ok: true };
  },
);
